import React, { useEffect, useState } from 'react';
import { Property } from '../../../interfaces/interfaces';
import { baseRealtyUrl, getUrlWithUtmQueryString, getTeams, CapCenterIcon } from '@capcenter/shared';
import { format as formatPhoneNumber } from 'libphonenumber-js';
import { GetImage } from '@capcenter/shared-legacy';

export const PresentedByPanel = (
	property: Property,
	isCapcenterListListing: boolean,
	isCapcenterBuyListing: boolean,
) => {
	const [teams, setTeams] = useState<any>({});

	// Listing Agent Info & Toggle
	const [listingAgent, setListingAgent] = useState<string>('');
	const [listingAgentProfile, setListingAgentProfile] = useState<any>({});
	const [displayListingSection, setDisplayListingSection] = useState<boolean>(false);

	// Buyers Agent Info & Toggle
	const [buyersAgent, setBuyersAgent] = useState<string>('');
	const [buyersAgentProfile, setBuyersAgentProfile] = useState<any>({});
	const [displayBuyersSection, setDisplayBuyersSection] = useState<boolean>(false);

	useEffect(() => {
		getTeams('realty', true)
			.then(response => setTeams(response))
			.catch(() => console.error('Failed to pull team profile, hiding panel'));

		if (isCapcenterListListing) {
			if (property.ListAgentFullName) {
				setListingAgent(property.ListAgentFullName);
			} else if (property.ListAgentFirstName && property.ListAgentLastName) {
				setListingAgent(`${property.ListAgentFirstName} ${property.ListAgentLastName}`);
			} else {
				setListingAgent('NoAgent');
			}
			setDisplayListingSection(true);
		} else {
			setListingAgent('RandomAgent');
		}

		if (isCapcenterBuyListing) {
			if (property.BuyerAgentFullName) {
				setBuyersAgent(property.BuyerAgentFullName);
			} else if (property.BuyerAgentFirstName && property.BuyerAgentLastName) {
				setBuyersAgent(`${property.BuyerAgentFirstName} ${property.BuyerAgentLastName}`);
			} else {
				setBuyersAgent('NoAgent');
			}
		}
	}, []);

	// Populate based on whether agent is found
	useEffect(() => {
		if (listingAgent === 'NoAgent') {
			setListingAgentProfile(
				{
					name: 'CapCenter Realty',
					email: 'customerservice@capcenter.com',
					phone: '+18009685844',
					image: '',
					website: `${baseRealtyUrl}/find-best-local-realtor/`,
					header: 'Listing Agency'
				}
			);
			
		} else if (Object.keys(teams).length > 0 && listingAgent) {
			if (listingAgent === 'RandomAgent') {
				const randomAgent = teams[Math.floor(Math.random() * (Object.keys(teams).length))];
				console.log(randomAgent);
				setListingAgentProfile(
					{
						name: randomAgent.name,
						email: (randomAgent.email ? `${randomAgent.email}@capcenter.com` : 'customerservice@capcenter.com'),
						phone: randomAgent.phone ?? '+18009685844',
						image: (randomAgent.image ?? GetImage(randomAgent) ?? ''),
						website: randomAgent.website ?? randomAgent.quote ?? `${baseRealtyUrl}/find-best-local-realtor/`,
						header: 'Realty Agent'
					}
				);
				setDisplayListingSection(true);
			} else {
				const matchingAgent = teams[Object.keys(teams).filter((agentProfile: any) => teams[agentProfile].name === listingAgent)[0]];
	
				if (matchingAgent) { // If we have the agent in our database, use that
					console.debug(matchingAgent);
					setListingAgentProfile(
						{
							name: matchingAgent.name,
							email: (matchingAgent.email ? `${matchingAgent.email}@capcenter.com` : property.ListAgentEmail ?? 'customerservice@capcenter.com'),
							phone: matchingAgent.phone ?? property.ListAgentPreferredPhone ?? '+18009685844',
							image: (matchingAgent.image ?? GetImage(matchingAgent) ?? ''),
							website: matchingAgent.website ?? matchingAgent.quote ?? `${baseRealtyUrl}/find-best-local-realtor/`,
							header: 'Listing Agent'
						}
					);
				} else { // Otherwise use the MLS data
					setListingAgentProfile(
						{
							name: listingAgent,
							email: property.ListAgentEmail ?? 'customerservice@capcenter.com',
							phone: property.ListAgentPreferredPhone ?? '+18009685844',
							image: '',
							website: `${baseRealtyUrl}/find-best-local-realtor/`,
							header: 'Listing Agent'
						}
					);
				}
			}
			setDisplayListingSection(true);
		}
	}, [listingAgent, teams]);

	useEffect(() => {
		if (buyersAgent === 'NoAgent') {
			setBuyersAgentProfile(
				{
					name: 'CapCenter Realty',
					email: 'customerservice@capcenter.com',
					phone: '+18009685844',
					image: '',
					website: `${baseRealtyUrl}/find-best-local-realtor/`,
					header: 'Buyer\'s Agency'
				}
			);
		} else if (Object.keys(teams).length > 0 && buyersAgent) {
			const matchingAgent = teams[Object.keys(teams).filter((agentProfile: any) => teams[agentProfile].name === buyersAgent)[0]];

			if (matchingAgent) { // If we have the agent in our database, use that
				console.debug(matchingAgent);
				setBuyersAgentProfile(
					{
						name: matchingAgent.name,
						email: `${matchingAgent.email}@capcenter.com` ?? 'customerservice@capcenter.com',
						phone: matchingAgent.phone ?? '+18009685844',
						image: (matchingAgent.image ?? GetImage(matchingAgent) ?? ''),
						website: matchingAgent.website ?? matchingAgent.quote ?? `${baseRealtyUrl}/find-best-local-realtor/`,
						header: 'Buyer\'s Agent'
					}
				);
			} else { // Otherwise use the MLS data
				setBuyersAgentProfile(
					{
						name: buyersAgent,
						email: 'customerservice@capcenter.com',
						phone: property.BuyerOfficePhone ?? '+18009685844',
						image: '',
						website: `${baseRealtyUrl}/find-best-local-realtor/`,
						header: 'Buyer\'s Agent'
					}
				);
			}
		}
	}, [buyersAgent, teams]);

	// Display info if it exists
	useEffect(() => {
		if (Object.keys(listingAgentProfile).length > 0) {
			setDisplayListingSection(true);
		}
	}, [listingAgentProfile]);

	useEffect(() => {
		if (Object.keys(buyersAgentProfile).length > 0) {
			setDisplayBuyersSection(true);
		}
	}, [buyersAgentProfile]);

	return (
		<React.Fragment>
			{ 
				displayListingSection 
				&& Object.keys(listingAgentProfile).length > 0 
				&& ProfileDisplay(listingAgentProfile)
			}

			{ 
				displayBuyersSection 
				&& Object.keys(buyersAgentProfile).length > 0
				&& ProfileDisplay(buyersAgentProfile)
			}
		</React.Fragment>
	);
};

const ProfileDisplay = (agentProfile: any) => {
	return <div className="row">
		<div className="col-12 col-md-8 p-3">
			<h5 className="pl-2 row">
				<span>
					<i className="cc-btn__icon cc-btn__icon--prefix fa fa-address-card-o" aria-hidden="true" />
					<>{agentProfile.header}:</>
				</span>
			</h5>
			<div className="pl-2 row agentInformationSection" style={{ maxHeight: '165px' }}>
				<div className="col-3 col-sm-4 m-auto" style={{ maxHeight: 'inherit' }}>
					{ agentProfile.image ?
						<img
							className="img-fluid rounded-circle"
							src={agentProfile.image}
							alt={'Profile picture of ' + agentProfile.name}
							style={{ maxHeight: 'inherit' }}
						/>
						:
						<div className='w-75 m-auto'><CapCenterIcon /></div>
					}
				</div>
				<div className="col-9 col-sm-8 text-nowrap">
					<span>
						{
							agentProfile.name && (
								agentProfile.name === 'CapCenter Realty'
									?
									<>
										<h4>
											<a href={getUrlWithUtmQueryString(agentProfile.website)} className='weight-bold'>{agentProfile.name}</a>
											<br />
										</h4>
									</>
									:<>
										<h4>
											<a href={getUrlWithUtmQueryString(agentProfile.website)}>{agentProfile.name}, REALTOR®</a>
											<br />
										</h4>
										<h5>
											Real Estate Agent
											<br />
										</h5>
									</>
							)
						}
						<h5>
							{agentProfile.email &&
							<a href={`mailto:${agentProfile.email}`}><i className="d-none d-sm-inline cc-btn__icon cc-btn__icon--prefix fas fa-envelope-open" aria-hidden="true" />{agentProfile.email}</a>
							}
						</h5>
						{
							agentProfile.phone && <h5>
								<a href={`tel:${formatPhoneNumber(agentProfile.phone, 'US', 'International_plaintext')}`}><i className="d-none d-sm-inline cc-btn__icon cc-btn__icon--prefix fas fa-phone" aria-hidden="true" />{formatPhoneNumber(agentProfile.phone, 'US', 'International')}</a>
								<br />
							</h5>
						}
						<h5>
							<a href={getUrlWithUtmQueryString(`${baseRealtyUrl}/forms/request-realty`)}>
								<i className="d-none d-sm-inline cc-btn__icon cc-btn__icon--prefix fas fa-users" aria-hidden="true" />
								Contact Our Team
							</a>
							<br />
						</h5>
					</span>
				</div>
			</div>
		</div>
	</div>;
};

export default PresentedByPanel;
